import React, { useState, useEffect, useMemo, useCallback } from 'react';
import useSimulationScores from '../hooks/useSimulationScores';
import { getTeamDocument, subscribeToGame } from '../firebase/db';
import { ATTRIBUTES, SEGMENTS, SEGMENT_NAMES } from '../scoring';
import AiInsightsPanel from './Professor/AiInsightsPanel';

const fmt = (val) => `$${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const styles = {
    container: {
        maxWidth: '900px', margin: '0 auto', padding: '20px',
        fontFamily: 'Arial, sans-serif',
    },
    heading: { textAlign: 'center', color: '#4b2e83', marginBottom: '6px' },
    subheading: { textAlign: 'center', color: '#666', fontSize: '14px', marginBottom: '24px' },
    cardGrid: {
        display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px', marginBottom: '28px',
    },
    card: {
        backgroundColor: '#f5f3fa', borderRadius: '10px', padding: '16px',
        textAlign: 'center', border: '1px solid #e0d8f0',
    },
    cardLabel: { fontSize: '11px', color: '#666', textTransform: 'uppercase', marginBottom: '4px' },
    cardValue: { fontSize: '24px', fontWeight: 'bold', color: '#4b2e83' },
    cardSub: { fontSize: '11px', color: '#999', marginTop: '3px' },
    sectionTitle: {
        fontSize: '16px', fontWeight: 'bold', color: '#4b2e83',
        marginBottom: '10px', marginTop: '24px',
    },
    table: {
        width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginBottom: '20px',
    },
    th: {
        backgroundColor: '#4b2e83', color: '#fff', padding: '8px 6px',
        textAlign: 'right', borderBottom: '2px solid #ddd',
    },
    thLeft: {
        backgroundColor: '#4b2e83', color: '#fff', padding: '8px 6px',
        textAlign: 'left', borderBottom: '2px solid #ddd',
    },
    td: { padding: '6px', textAlign: 'right', borderBottom: '1px solid #eee' },
    tdLeft: { padding: '6px', textAlign: 'left', borderBottom: '1px solid #eee', fontWeight: 'bold' },
    noData: {
        textAlign: 'center', color: '#999', padding: '40px 20px', fontStyle: 'italic',
        backgroundColor: '#f9f9f9', borderRadius: '8px',
    },
    myRow: { backgroundColor: '#f0ebf8' },
};

const StudentResults = ({ teamId }) => {
    const [teamName, setTeamName] = useState('');
    const [teamType, setTeamType] = useState('');
    const [gameId, setGameId] = useState(null);
    const [releasedRounds, setReleasedRounds] = useState([]);
    const [activeTeams, setActiveTeams] = useState(null);
    const [activeRounds, setActiveRounds] = useState(null);
    const [initLoading, setInitLoading] = useState(true);
    const [noGame, setNoGame] = useState(false);

    // Parse teamId into teamName and teamType
    useEffect(() => {
        if (!teamId) return;
        const lastUnderscore = teamId.lastIndexOf('_');
        if (lastUnderscore === -1) {
            setTeamName(teamId);
            setTeamType('Master');
        } else {
            setTeamName(teamId.substring(0, lastUnderscore));
            setTeamType(teamId.substring(lastUnderscore + 1));
        }
    }, [teamId]);

    // Fetch team doc to get gameId
    useEffect(() => {
        if (!teamName || !teamType) return;
        const loadTeam = async () => {
            const doc = await getTeamDocument(teamName, teamType);
            if (doc?.teamData?.gameId) {
                setGameId(doc.teamData.gameId);
            } else {
                setNoGame(true);
                setInitLoading(false);
            }
        };
        loadTeam();
    }, [teamName, teamType]);

    // Subscribe to game doc for releasedRounds
    useEffect(() => {
        if (!gameId) return;
        const unsubscribe = subscribeToGame(gameId, (game) => {
            setReleasedRounds(game.releasedRounds || []);
            setActiveTeams(game.activeTeams || null);
            setActiveRounds(game.activeRounds || null);
            setInitLoading(false);
        });
        return () => unsubscribe();
    }, [gameId]);

    // Use the scoring hook (only when we have gameId)
    const { scores, teams, loading: scoresLoading, computeScores } = useSimulationScores(
        teamType || 'Master',
        gameId
    );

    // Apply professor's activeTeams filter if set
    const effectiveTeams = useMemo(() => {
        if (!activeTeams || activeTeams.length === 0) return teams;
        const filtered = teams.filter(t => activeTeams.includes(t.teamName));
        return filtered.length > 0 ? filtered : teams;
    }, [teams, activeTeams]);

    const effectiveScores = useMemo(() => {
        if (!activeTeams || activeTeams.length === 0 || effectiveTeams.length === teams.length) return scores;
        return computeScores(effectiveTeams);
    }, [activeTeams, effectiveTeams, teams, scores, computeScores]);

    const loading = initLoading || (gameId && scoresLoading);
    const roundNums = activeRounds && activeRounds.length > 0
        ? activeRounds.sort((a, b) => a - b)
        : [1, 2, 3];

    // Compute team aggregates
    const teamAgg = { totalProfit: 0, totalRevenue: 0, totalUnits: 0 };
    const teamRoundData = {};
    const teamRanks = {};
    const allTeamNames = effectiveTeams.map(t => t.teamName).sort();

    roundNums.forEach(r => {
        const rs = effectiveScores[`round${r}`];
        if (!rs || !releasedRounds.includes(r)) return;
        const myData = rs[teamName];
        if (myData) {
            teamRoundData[r] = myData;
            teamAgg.totalProfit += myData.totalProfit || 0;
            teamAgg.totalRevenue += myData.totalRevenue || 0;
            teamAgg.totalUnits += myData.totalUnits || 0;
        }
        // Compute rank
        const profitList = allTeamNames
            .filter(n => rs[n])
            .map(n => ({ name: n, profit: rs[n].totalProfit || 0 }))
            .sort((a, b) => b.profit - a.profit);
        const idx = profitList.findIndex(p => p.name === teamName);
        teamRanks[r] = idx >= 0 ? idx + 1 : null;
    });

    // Overall rank
    const overallProfits = allTeamNames.map(name => {
        let p = 0;
        roundNums.forEach(r => {
            const rs = effectiveScores[`round${r}`];
            if (rs && releasedRounds.includes(r) && rs[name]) {
                p += rs[name].totalProfit || 0;
            }
        });
        return { name, profit: p };
    }).sort((a, b) => b.profit - a.profit);
    const overallRank = overallProfits.findIndex(p => p.name === teamName) + 1;
    const totalTeams = allTeamNames.length;

    const releasedCount = releasedRounds.length;
    const myTeamIncluded = !activeTeams || activeTeams.length === 0 || activeTeams.includes(teamName);

    const visibleRounds = roundNums.filter(r => releasedRounds.includes(r));

    const buildStudentSummary = useCallback(() => {
        const summary = {
            marketContext: {
                segments: SEGMENT_NAMES.map(name => ({
                    name,
                    population: SEGMENTS[name].population,
                    preferences: SEGMENTS[name].preferences,
                })),
            },
            teams: {},
            activeRounds: visibleRounds,
        };

        allTeamNames.forEach(name => {
            const team = effectiveTeams.find(t => t.teamName === name);
            const teamEntry = { rounds: {}, cumulative: { totalProfit: 0, totalUnits: 0, totalRevenue: 0 } };

            visibleRounds.forEach(r => {
                const rs = effectiveScores[`round${r}`];
                if (!rs?.[name]) return;
                const result = rs[name];

                const totalUnitsInRound = allTeamNames.reduce((s, n) => s + (rs[n]?.totalUnits || 0), 0);
                const mktShare = totalUnitsInRound > 0
                    ? ((result.totalUnits / totalUnitsInRound) * 100).toFixed(1) + '%' : '0%';

                const profitList = allTeamNames.filter(n => rs[n])
                    .map(n => ({ n, p: rs[n].totalProfit || 0 }))
                    .sort((a, b) => b.p - a.p);
                const rank = profitList.findIndex(p => p.n === name) + 1;

                // For the student's own team, include full data; for others, only bike attributes + results
                const roundEntry = {
                    bike: result.bike || {},
                    results: {
                        units: result.totalUnits, revenue: result.totalRevenue,
                        profit: result.totalProfit, marketSharePct: mktShare, rank,
                    },
                };

                // Only include pricing and ad data for the student's own team
                if (name === teamName) {
                    const rd = team?.teamData?.rounds?.[r - 1];
                    roundEntry.price = parseFloat(rd?.profit_margin?.price_per_bike_dollars) || 0;
                    const perfTotal = Object.values(rd?.advertising?.performance_ads || {}).reduce((s, v) => s + (v || 0), 0);
                    const brandTotal = Object.values(rd?.advertising?.brand_ads || {}).reduce((s, v) => s + (v || 0), 0);
                    roundEntry.adSpend = {
                        total: perfTotal + brandTotal,
                        segments: rd?.advertising?.segments || [],
                        perfTotal, brandTotal,
                    };
                    roundEntry.profitMarginPct = result.totalRevenue > 0
                        ? ((result.totalProfit / result.totalRevenue) * 100).toFixed(1) + '%' : '0%';
                }

                teamEntry.rounds[r] = roundEntry;
                teamEntry.cumulative.totalProfit += result.totalProfit || 0;
                teamEntry.cumulative.totalUnits += result.totalUnits || 0;
                teamEntry.cumulative.totalRevenue += result.totalRevenue || 0;
            });

            summary.teams[name] = teamEntry;
        });

        // Add overall ranks
        const ranked = Object.entries(summary.teams)
            .sort(([, a], [, b]) => b.cumulative.totalProfit - a.cumulative.totalProfit);
        ranked.forEach(([n], i) => { summary.teams[n].cumulative.overallRank = i + 1; });

        return summary;
    }, [effectiveScores, effectiveTeams, allTeamNames, visibleRounds, teamName]);

    const insightFilterHash = `teams:${activeTeams ? activeTeams.sort().join(',') : 'all'}|rounds:${roundNums.join(',')}`;

    if (loading) {
        return <div style={styles.noData}>Loading results...</div>;
    }

    if (noGame) {
        return (
            <div style={styles.container}>
                <h2 style={styles.heading}>My Results</h2>
                <div style={styles.noData}>
                    Your team is not linked to a game. Results are not available.
                </div>
            </div>
        );
    }

    if (releasedCount === 0) {
        return (
            <div style={styles.container}>
                <h2 style={styles.heading}>My Results</h2>
                <p style={styles.subheading}>Team: {teamName}</p>
                <div style={styles.noData}>
                    Results have not been released yet.<br />
                    Check back after your instructor has reviewed the round.
                </div>
            </div>
        );
    }

    if (!myTeamIncluded) {
        return (
            <div style={styles.container}>
                <h2 style={styles.heading}>My Results</h2>
                <p style={styles.subheading}>Team: {teamName}</p>
                <div style={styles.noData}>
                    Your team's results are not currently available.<br />
                    Check back after your instructor updates the results.
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <h2 style={styles.heading}>My Results</h2>
            <p style={styles.subheading}>Team: {teamName}</p>

            {/* KPI Cards */}
            <div style={styles.cardGrid}>
                <div style={styles.card}>
                    <div style={styles.cardLabel}>Total Profit</div>
                    <div style={styles.cardValue}>{fmt(teamAgg.totalProfit)}</div>
                    <div style={styles.cardSub}>{releasedCount} round{releasedCount !== 1 ? 's' : ''}</div>
                </div>
                <div style={styles.card}>
                    <div style={styles.cardLabel}>Total Revenue</div>
                    <div style={styles.cardValue}>{fmt(teamAgg.totalRevenue)}</div>
                </div>
                <div style={styles.card}>
                    <div style={styles.cardLabel}>Units Sold</div>
                    <div style={styles.cardValue}>{teamAgg.totalUnits.toLocaleString()}</div>
                </div>
                <div style={styles.card}>
                    <div style={styles.cardLabel}>Profit Rank</div>
                    <div style={styles.cardValue}>#{overallRank}</div>
                    <div style={styles.cardSub}>of {totalTeams} teams</div>
                </div>
            </div>

            {/* AI Insights */}
            {gameId && releasedCount > 0 && (
                <AiInsightsPanel
                    gameId={gameId}
                    cacheDocId={`student_${teamName}`}
                    filterHash={insightFilterHash}
                    buildSummary={buildStudentSummary}
                    insightType="student"
                    teamName={teamName}
                />
            )}

            {/* Round-by-Round Results */}
            <div style={styles.sectionTitle}>Round-by-Round Performance</div>
            <table style={styles.table}>
                <thead>
                    <tr>
                        <th style={styles.thLeft}>Metric</th>
                        {roundNums.map(r => (
                            <th key={r} style={styles.th}>Round {r}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td style={styles.tdLeft}>Your Profit</td>
                        {roundNums.map(r => (
                            <td key={r} style={styles.td}>
                                {releasedRounds.includes(r) && teamRoundData[r]
                                    ? fmt(teamRoundData[r].totalProfit)
                                    : <span style={{ color: '#ccc' }}>-</span>}
                            </td>
                        ))}
                    </tr>
                    <tr>
                        <td style={styles.tdLeft}>Your Revenue</td>
                        {roundNums.map(r => (
                            <td key={r} style={styles.td}>
                                {releasedRounds.includes(r) && teamRoundData[r]
                                    ? fmt(teamRoundData[r].totalRevenue)
                                    : <span style={{ color: '#ccc' }}>-</span>}
                            </td>
                        ))}
                    </tr>
                    <tr>
                        <td style={styles.tdLeft}>Your Units</td>
                        {roundNums.map(r => (
                            <td key={r} style={styles.td}>
                                {releasedRounds.includes(r) && teamRoundData[r]
                                    ? teamRoundData[r].totalUnits.toLocaleString()
                                    : <span style={{ color: '#ccc' }}>-</span>}
                            </td>
                        ))}
                    </tr>
                    <tr>
                        <td style={styles.tdLeft}>Your Rank</td>
                        {roundNums.map(r => (
                            <td key={r} style={{ ...styles.td, fontWeight: 'bold', color: '#4b2e83' }}>
                                {releasedRounds.includes(r) && teamRanks[r]
                                    ? `#${teamRanks[r]}`
                                    : <span style={{ color: '#ccc' }}>-</span>}
                            </td>
                        ))}
                    </tr>
                    <tr>
                        <td style={styles.tdLeft}>Total Market Revenue</td>
                        {roundNums.map(r => {
                            const rs = effectiveScores[`round${r}`];
                            if (!releasedRounds.includes(r) || !rs) return <td key={r} style={styles.td}><span style={{ color: '#ccc' }}>-</span></td>;
                            const total = allTeamNames.reduce((s, n) => s + (rs[n]?.totalRevenue || 0), 0);
                            return <td key={r} style={styles.td}>{fmt(total)}</td>;
                        })}
                    </tr>
                    <tr>
                        <td style={styles.tdLeft}>Your Market Share</td>
                        {roundNums.map(r => {
                            const rs = effectiveScores[`round${r}`];
                            if (!releasedRounds.includes(r) || !rs || !teamRoundData[r]) return <td key={r} style={styles.td}><span style={{ color: '#ccc' }}>-</span></td>;
                            const totalUnits = allTeamNames.reduce((s, n) => s + (rs[n]?.totalUnits || 0), 0);
                            const share = totalUnits > 0 ? ((teamRoundData[r].totalUnits / totalUnits) * 100).toFixed(1) : '0.0';
                            return <td key={r} style={styles.td}>{share}%</td>;
                        })}
                    </tr>
                </tbody>
            </table>
            <p style={{ fontSize: '11px', color: '#aaa', fontStyle: 'italic', marginTop: '-12px' }}>
                Rounds marked with "-" have not been released by your instructor yet.
            </p>

            {/* Your Segment Performance */}
            <div style={styles.sectionTitle}>Your Segment Performance</div>
            {releasedRounds.sort((a, b) => a - b).map(r => {
                const rd = teamRoundData[r];
                if (!rd?.segmentResults) return null;
                return (
                    <div key={r} style={{ marginBottom: '16px' }}>
                        <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#333', marginBottom: '6px' }}>
                            Round {r}
                        </div>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.thLeft}>Segment</th>
                                    <th style={styles.th}>Units Sold</th>
                                    <th style={styles.th}>Revenue</th>
                                    <th style={styles.th}>Profit</th>
                                    <th style={styles.th}>Share</th>
                                </tr>
                            </thead>
                            <tbody>
                                {SEGMENT_NAMES.map(seg => {
                                    const sr = rd.segmentResults[seg];
                                    if (!sr) return null;
                                    return (
                                        <tr key={seg}>
                                            <td style={styles.tdLeft}>{seg}</td>
                                            <td style={styles.td}>{(sr.units || 0).toLocaleString()}</td>
                                            <td style={styles.td}>{fmt(sr.revenue || 0)}</td>
                                            <td style={styles.td}>{fmt(sr.profit || 0)}</td>
                                            <td style={styles.td}>{((sr.share || 0) * 100).toFixed(1)}%</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                );
            })}

            {/* Research Report - all teams' bike attributes */}
            <div style={styles.sectionTitle}>Market Research Report</div>
            <p style={{ fontSize: '12px', color: '#888', marginTop: '-6px', marginBottom: '12px' }}>
                Bike attributes for all teams in the market (public data).
            </p>
            {releasedRounds.sort((a, b) => a - b).map(r => {
                const rs = effectiveScores[`round${r}`];
                if (!rs) return null;
                const teamsWithScores = allTeamNames.filter(n => rs[n]?.bike);
                if (teamsWithScores.length === 0) return null;
                return (
                    <div key={r} style={{ marginBottom: '20px' }}>
                        <div style={{
                            fontSize: '14px', fontWeight: 'bold', color: '#4b2e83',
                            marginBottom: '6px', borderBottom: '2px solid #4b2e83', paddingBottom: '4px',
                        }}>
                            Round {r}
                        </div>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.thLeft}>Team</th>
                                    {ATTRIBUTES.map(attr => (
                                        <th key={attr} style={{ ...styles.th, textAlign: 'center', fontSize: '11px' }}>
                                            {attr}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {teamsWithScores.map(name => (
                                    <tr key={name} style={name === teamName ? styles.myRow : {}}>
                                        <td style={{
                                            ...styles.tdLeft,
                                            color: name === teamName ? '#4b2e83' : '#333',
                                        }}>
                                            {name}{name === teamName ? ' (you)' : ''}
                                        </td>
                                        {ATTRIBUTES.map(attr => (
                                            <td key={attr} style={{
                                                ...styles.td, textAlign: 'center',
                                                fontWeight: name === teamName ? 'bold' : 'normal',
                                            }}>
                                                {rs[name].bike[attr]}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                );
            })}
        </div>
    );
};

export default StudentResults;
